<script setup lang="ts">
import { onMounted, computed } from 'vue';
import { graphqlQuery, AUTHENTICATE_GOOGLE_ID_MUTATION, type AuthenticateGoogleIdResponse } from '../../graphql';
import { completeAuthentication, setAuthCookies } from '../../services/authCookies';
import { errorToast, successToast } from '../../services/toasts';

const googleClientId = computed(() => window.__GOOGLE_CLIENT_ID__ || '');
const oauthClientId = computed(() => window.__OAUTH_CLIENT_ID__ || '');
const isEnabled = computed(() => !!googleClientId.value && !!oauthClientId.value);

async function onSignIn(googleResponse: GoogleCredentialResponse) {
  try {
    const response = await graphqlQuery<AuthenticateGoogleIdResponse>(AUTHENTICATE_GOOGLE_ID_MUTATION, {
      idToken: googleResponse.credential,
      clientId: oauthClientId.value,
    });

    const accessToken = response.authenticateGoogleId?.accessToken;
    const errors = response.authenticateGoogleId?.errors;

    if (accessToken?.token) {
      setAuthCookies(accessToken);
      successToast('Successfully authenticated');
      completeAuthentication();
      return;
    }

    if (errors) {
      errorToast(`Unable to authenticate: ${errors.message}`);
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'An error occurred';
    errorToast(message);
  }
}

onMounted(() => {
  if (!isEnabled.value) return;

  const checkGoogleLoaded = setInterval(() => {
    if (window.google?.accounts?.id) {
      clearInterval(checkGoogleLoaded);

      window.google.accounts.id.initialize({
        client_id: googleClientId.value,
        callback: onSignIn,
      });

      const buttonElement = document.getElementById('google-signin-button');
      if (buttonElement) {
        window.google.accounts.id.renderButton(buttonElement, {
          theme: 'outline',
          size: 'large',
          type: 'standard',
          shape: 'rectangular',
          text: 'signin_with',
          logo_alignment: 'left',
          width: '340',
        });
      }
    }
  }, 100);

  setTimeout(() => clearInterval(checkGoogleLoaded), 5000);
});
</script>

<template>
  <div v-if="isEnabled" class="flex justify-center w-sso-button">
    <div id="google-signin-button"></div>
  </div>
</template>
